import { Shield, Users, TrendingUp, Award, Target, Heart, CheckCircle, Star } from 'lucide-react';
import Header from '@/react-app/components/Header';
import Footer from '@/react-app/components/Footer';
import { useNavigate } from 'react-router';
import { useAuthStore } from '@/shared/stores/authStore';
import ceo from "../../assets/ceo.jpeg";
import regManager from "../../assets/reg-manager.jpeg";
import branchManager from "../../assets/branch-manag.jpeg";




export default function About() {

  const { isAuthenticated } = useAuthStore();
  const navigate = useNavigate();

  const handleGetStarted = () => {
    if (isAuthenticated) {
      navigate('/save');
    } else {
      navigate('/login');
    }
  };

  return (
    <div className="min-h-screen bg-white">
      <Header />

      {/* Hero Section */}
      <section className="pt-16 bg-gradient-to-br from-blue-50 via-white to-purple-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
          <div className="text-center max-w-4xl mx-auto">
            <h1 className="text-5xl lg:text-6xl font-bold text-gray-900 mb-6">
              About <span className="gradient-text">Trade Enrich</span>
            </h1>
            <p className="text-xl text-gray-600 leading-relaxed">
              We're on a mission to democratize wealth building by making smart savings accessible to everyone.
              Through innovative technology and transparent practices, we help people save smarter, earn consistently,
              and build lasting financial freedom.
            </p>
          </div>
        </div>
      </section>

      {/* Mission & Vision */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <div className="bg-blue-100 rounded-full w-16 h-16 flex items-center justify-center mb-6">
                <Target className="h-8 w-8 text-blue-600" />
              </div>
              <h2 className="text-3xl font-bold text-gray-900 mb-4">Our Mission</h2>
              <p className="text-lg text-gray-600 leading-relaxed mb-6">
                To empower individuals and families to achieve financial independence through smart savings strategies
                that deliver consistent returns and build long-term wealth.
              </p>
              <div className="space-y-3">
                <div className="flex items-center space-x-3">
                  <CheckCircle className="h-5 w-5 text-green-500" />
                  <span className="text-gray-700">Transparent fee structure with no hidden costs</span>
                </div>
                <div className="flex items-center space-x-3">
                  <CheckCircle className="h-5 w-5 text-green-500" />
                  <span className="text-gray-700">Consistent monthly returns up to 10%</span>
                </div>
                <div className="flex items-center space-x-3">
                  <CheckCircle className="h-5 w-5 text-green-500" />
                  <span className="text-gray-700">Secure platform with bank-level encryption</span>
                </div>
              </div>
            </div>
            <div>
              <div className="bg-purple-100 rounded-full w-16 h-16 flex items-center justify-center mb-6">
                <Heart className="h-8 w-8 text-purple-600" />
              </div>
              <h2 className="text-3xl font-bold text-gray-900 mb-4">Our Vision</h2>
              <p className="text-lg text-gray-600 leading-relaxed">
                To become the leading platform for accessible wealth building, where every person has the tools
                and opportunity to create a financially secure future for themselves and their families. We envision
                a world where smart savings and consistent returns are available to everyone, regardless of their
                starting point or financial background.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Our Story */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold text-gray-900 mb-8">Our Story</h2>
          <div className="prose prose-lg mx-auto text-gray-600">
            <p className="mb-6">
              Trade Enrich was founded in 2023 with a simple belief: everyone deserves access to smart financial tools
              that can help them build wealth over time. Our founders, experienced in both traditional finance and
              modern fintech, saw the gap between complex investment products and simple savings accounts.
            </p>
            <p className="mb-6">
              We created Trade Enrich to bridge that gap, offering savings packages that provide consistent returns
              while being easy to understand and accessible to everyone. Our platform combines the security of
              traditional banking with the innovation of modern financial technology.
            </p>
            <p>
              Today, we're proud to serve thousands of savers who trust us with their financial future, and we're
              just getting started on our mission to democratize wealth building for all.
            </p>
          </div>
        </div>
      </section>

      {/* Core Values */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Our Core Values</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              These principles guide everything we do and shape how we serve our community of savers.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {[
              {
                icon: Shield,
                title: 'Trust & Security',
                description: 'Your financial security is our top priority. We use industry-leading encryption and security measures.',
                color: 'blue'
              },
              {
                icon: Users,
                title: 'Community First',
                description: 'We believe in building a supportive community where everyone can learn and grow together.',
                color: 'green'
              },
              {
                icon: TrendingUp,
                title: 'Consistent Growth',
                description: 'We focus on delivering steady, reliable returns that compound over time to build real wealth.',
                color: 'purple'
              },
              {
                icon: Award,
                title: 'Excellence',
                description: 'We strive for excellence in everything we do, from our platform to our customer service.',
                color: 'orange'
              }
            ].map((value, index) => (
              <div key={index} className="text-center">
                <div className={`bg-${value.color}-100 rounded-2xl w-20 h-20 flex items-center justify-center mx-auto mb-6`}>
                  <value.icon className={`h-10 w-10 text-${value.color}-600`} />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">{value.title}</h3>
                <p className="text-gray-600">{value.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-20 bg-gradient-to-r from-blue-600 to-purple-600">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center text-white mb-12">
            <h2 className="text-3xl font-bold mb-4">Our Impact in Numbers</h2>
            <p className="text-xl text-blue-100">
              See how we're helping people build wealth and achieve financial freedom
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {[
              { number: '5,000+', label: 'Happy Savers', icon: Users },
              { number: '$2M+', label: 'Total Saved', icon: TrendingUp },
              { number: '$200K+', label: 'Returns Paid', icon: Award },
              { number: '98%', label: 'Satisfaction Rate', icon: Star }
            ].map((stat, index) => (
              <div key={index} className="text-center">
                <div className="bg-white/10 backdrop-blur-lg rounded-2xl p-8">
                  <stat.icon className="h-12 w-12 text-blue-200 mx-auto mb-4" />
                  <div className="text-4xl font-bold text-white mb-2">{stat.number}</div>
                  <div className="text-blue-200">{stat.label}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Leadership Team */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Leadership Team</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Meet the experienced professionals leading Trade Enrich's mission to democratize wealth building.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[
              {
                name: 'Mr. Anthony Ibe',
                role: 'Chief Executive Officer',
                bio: 'The Chief Executive Officer of Trade Enrich is responsible for defining the company’s strategic direction and ensuring sustainable growth. The CEO provides executive oversight, strengthens corporate governance, and leads initiatives that enhance Trade Enrich’s reputation as a trusted provider of trade and business solutions.',
                image: ceo
              },
              {
                name: 'Miriam Bassey',
                role: 'Regional Manager',
                bio: 'The Regional Manager provides leadership and strategic coordination across multiple branches. They ensure compliance, drive performance consistency, and execute regional strategies that contribute to Trade Enrich’s overall growth and operational excellence.',
                image: regManager
              },
              {
                name: 'Desmond Oke',
                role: 'Branch Manager',
                bio: 'The Branch Manager manages branch operations in line with Trade Enrich’s corporate policies and performance standards. They ensure operational efficiency, oversee staff productivity, and maintain strong client relationships to support the company’s business objectives.',
                image: branchManager
              }
            ].map((member, index) => (
              <div key={index} className="bg-white rounded-2xl p-8 shadow-lg text-center">
                <img
                  src={member.image}
                  alt={member.name}
                  className="w-24 h-24 rounded-full mx-auto mb-4 object-cover"
                />
                <h3 className="text-xl font-bold text-gray-900 mb-2">{member.name}</h3>
                <p className="text-blue-600 font-medium mb-3">{member.role}</p>
                <p className="text-gray-600 text-sm">{member.bio}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Join Us CTA */}
      <section className="py-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold text-gray-900 mb-6">
            Ready to Start Your Wealth Building Journey?
          </h2>
          <p className="text-xl text-gray-600 mb-8">
            Join thousands of smart savers who are already building their financial future with Trade Enrich.
          </p>
          <button
            onClick={handleGetStarted}
            className="bg-blue-600 text-white px-8 py-4 rounded-xl text-lg font-semibold hover:bg-blue-700 transition-all duration-300 shadow-2xl hover:shadow-3xl inline-block"
          >
            Get Started Today
          </button>
        </div>
      </section>

      <Footer />
    </div>
  );
}
